import {
  Application,
  utils,
  Sprite,
  Point,
  filters,
  Graphics,
  Text,
} from 'pixi.js';
import gsap from 'gsap';
import { padStart } from 'lodash';
import { setCover, isMobile, isTouchDevice } from '../utils';
import assets from './assets';
import store from '@/store';

class WebGLOverlay {
  constructor(DOMElement, DOMContainer) {
    this.DOMElement = DOMElement;
    this.DOMContainer = DOMContainer;
    this.DOMRect = this.DOMContainer.getBoundingClientRect();
    this.childrens = [];

    utils.skipHello();
    this.app = new Application({
      view: this.DOMElement,
      resolution: 1,
      antialias: false,
      transparent: true,
    });
    this.app.ticker.stop();

    this.background = new Sprite();
    this.app.stage.addChild(this.background);

    this.displace = null;
    this.noise = null;

    store.commit('SET_POINTER', 'loading');

    // Fill all the resources into the loader
    assets.forEach((asset) => {
      this.app.loader.add(asset.name, asset.url);
    });
    this.app.loader.load(() => {
      this.textureLoaded();
      window.dispatchEvent(new CustomEvent('webgl-loaded'));
      window.dispatchEvent(new CustomEvent('change-mode'));

      this.renderHandler = () => this.render();
      this.app.ticker.add(this.renderHandler);

      // Prevent PIXI from creating texture ahead of time
      // TOFIX: better loading handle
      setTimeout(() => {
        store.commit('SET_LOADED', true);
        store.commit('SET_POINTER', 'default');
        this.app.ticker.start();
      }, 1000);
    });

    window.addEventListener('audio-pause', () => { this.play.alpha = 1; this.pause.alpha = 0; });
    window.addEventListener('audio-play', () => { this.play.alpha = 0; this.pause.alpha = 1; });
    window.addEventListener('change-mode', () => { this.changeUIColor(); });
  }

  push(element) {
    this.childrens.push(element);
    this.app.stage.addChild(element.sprite);
    element.init();
  }

  render() {
    // this.childrens.forEach(e => e.render());
    window.dispatchEvent(new CustomEvent('render'));
    this.app.stage.sortChildren(); // TODO: Check performance issue
  }

  resize() {
    this.DOMRect = this.DOMContainer.getBoundingClientRect();
    this.app.view.width = this.DOMRect.width;
    this.app.view.height = this.DOMRect.height;

    this.background.width = this.DOMRect.width;
    this.background.height = this.DOMRect.height;

    this.setDisplace();
    setCover(this.noise, this.background);
    this.setNoiseWhite();
    this.setRecBall();
    this.setRecText();
    this.setCrono();
    this.setTime();
    this.setContactUs();
    this.setUpper();

    this.childrens.forEach(e => e.resize());
  }

  textureLoaded() {
    this.displaceSprite = new Sprite(this.app.loader.resources.displace.texture);
    this.displaceSprite.anchor = new Point(0.5, 0.5);
    this.displaceSprite.originalWidth = this.displaceSprite.width;
    this.setDisplace();
    this.app.stage.addChild(this.displaceSprite);

    this.displace = new filters.DisplacementFilter(this.displaceSprite, isTouchDevice() ? 20 : 100);
    this.displace.padding = 200;

    this.app.loader.resources.noise.data.muted = 'muted';
    this.app.loader.resources.noise.data.autoplay = true;
    this.app.loader.resources.noise.data.loop = true;
    this.app.loader.resources.noise.data.setAttribute('playsinline', true);
    this.noise = Sprite.from(this.app.loader.resources.noise.data);
    this.app.stage.addChild(this.noise);
    setCover(this.noise, this.background);

    this.noiseWhite = new Graphics();
    this.noiseWhite.beginFill(0xffffff)
      .drawRect(0, 0, this.background.width, this.background.height)
      .endFill();
    this.setNoiseWhite();
    // this.app.stage.addChild(this.noiseWhite);
    this.noiseWhite.mask = this.noise;
    this.noiseWhite.filters = [this.displace];

    this.rec = new Sprite();
    this.recBall = new Text('•', {
      fill: 0xff0000,
      fontFamily: 'VHS',
      fontSize: 60,
    });
    this.recBall.anchor = new Point(1, 0.5);
    this.setRecBall();
    this.recText = new Text('REC', {
      fill: 0xffffff,
      fontFamily: 'VHS',
      fontSize: 30,
    });
    this.recText.anchor = new Point(1, 0.5);
    this.setRecText();
    this.rec.addChild(this.recBall);
    this.rec.addChild(this.recText);
    this.app.stage.addChild(this.rec);
    this.rec.filters = [this.displace];
    this.rec.zIndex = 2;

    this.time = new Sprite();
    this.play = new Text('SOUND ON', {
      fill: 0xffffff,
      fontFamily: 'VHS',
      fontSize: 30,
    });
    this.pause = new Text('MUTE', {
      fill: 0xffffff,
      fontFamily: 'VHS',
      fontSize: 30,
    });
    this.pause.alpha = 0;
    this.time.addChild(this.play);
    this.time.addChild(this.pause);
    const data = new Date();
    this.crono = new Text(`${padStart(data.getDate(), 2, '0')}/${padStart(data.getMonth() + 1, 2, '0')}/${data.getFullYear()} ${padStart(data.getHours(), 2, '0')}:${padStart(data.getMinutes(), 2, '0')}`, {
      fill: 0xffffff,
      fontFamily: 'VHS',
      fontSize: 30,
    });
    this.setCrono();
    this.time.addChild(this.crono);
    this.setTime();
    this.app.stage.addChild(this.time);
    this.time.filters = [this.displace];
    this.time.zIndex = 2;

    const tl = gsap.timeline({ repeat: -1 });
    tl.to(this.recBall, { alpha: 0 })
      .to(this.recBall, { alpha: 1, delay: 0.2 });

    this.upper = new Sprite();
    this.changeMode = new Text('CHANGE MODE', {
      fill: 0xffffff,
      fontFamily: 'VHS',
      fontSize: isTouchDevice() ? 20 : 30,
    });
    this.upper.addChild(this.changeMode);
    this.contactUs = new Text('CONTACT US', {
      fill: 0xffffff,
      fontFamily: 'VHS',
      fontSize: isTouchDevice() ? 20 : 30,
    });
    this.setContactUs();
    this.upper.addChild(this.contactUs);
    this.setUpper();
    this.app.stage.addChild(this.upper);
    this.upper.filters = [this.displace];
    this.upper.zIndex = 2;
  }

  setDisplace() {
    if (!this.displaceSprite) return;
    const h = Math.max(window.innerWidth, window.innerHeight);
    const r = 0.5 + h / this.displaceSprite.originalWidth;
    this.displaceSprite.scale = new Point(r, r);
    this.displaceSprite.position = new Point(window.innerWidth / 2, window.innerHeight / 2);
  }

  setNoiseWhite() {
    if (!this.noiseWhite) return;
    this.noiseWhite.width = this.background.width;
    this.noiseWhite.height = this.background.height;
  }

  setRecBall() {
    if (!this.recBall) return;
    this.recBall.position.y = (window.innerHeight / 18) * 2;
    this.recBall.position.x = (window.innerWidth / 2) - 10;
    if (isMobile()) this.recBall.renderable = false;
    else this.recBall.renderable = true;
  }

  setRecText() {
    if (!this.recText) return;
    this.recText.position.y = (window.innerHeight / 18) * 2;
    this.recText.position.x = (window.innerWidth / 2) + 50;
    if (isMobile()) this.recText.renderable = false;
    else this.recText.renderable = true;
  }

  setCrono() {
    if (!this.crono) return;
    this.crono.position.x = window.innerWidth - (this.crono.width + (window.innerWidth / 32) * 4);
  }

  setTime() {
    if (!this.time) return;
    this.time.position.x = (window.innerWidth / 32) * 2;
    this.time.position.y = window.innerHeight - (window.innerHeight / 18) * 2;
    if (isMobile()) this.time.renderable = false;
    else this.time.renderable = true;
  }

  setContactUs() {
    if (!this.contactUs) return;
    this.contactUs.position.x = window.innerWidth - (this.contactUs.width + (window.innerWidth / 18) * 2);
  }

  setUpper() {
    if (!this.upper) return;
    this.upper.position.x = (window.innerWidth / 32) * 2;
    this.upper.position.y = (window.innerHeight / 18) * 2;
  }

  pauseTicker() {
    this.app.ticker.remove(this.renderHandler);
  }

  playTicker() {
    this.app.ticker.add(this.renderHandler);
  }

  switchMode() {
    for (let i = this.childrens.length - 1; i >= 0; i -= 1) {
      delete this.childrens[i];
      this.childrens.splice(i, 1);
    }
  }

  changeUIColor() {
    if (store.state.mode === 'estate') {
      this.play.style.fill = '0x000000';
      this.changeMode.style.fill = '0x000000';
      this.recText.style.fill = '0x000000';
      this.contactUs.style.fill = '0x000000';
      this.crono.style.fill = '0x000000';
      this.play.dirty = true;
      this.changeMode.dirty = true;
      this.recText.dirty = true;
      this.contactUs.dirty = true;
      this.crono.dirty = true;
    } else {
      this.play.style.fill = '0xffffff';
      this.changeMode.style.fill = '0xffffff';
      this.recText.style.fill = '0xffffff';
      this.contactUs.style.fill = '0xffffff';
      this.crono.style.fill = '0xffffff';
      this.play.dirty = true;
      this.changeMode.dirty = true;
      this.recText.dirty = true;
      this.contactUs.dirty = true;
      this.crono.dirty = true;
    }
  }
}

export default WebGLOverlay;
